import { createHook, createSelector, type BoundActions } from 'react-sweet-state';

import type { State, MappingState } from '../../../../common/types';
import type { Actions } from '../../actions';
import { Store } from '../../main';

const mappingSelector = (state: State): MappingState | null => state.mappingState;

export type UseMappingState = () => [MappingState | null, Partial<BoundActions<State, Actions>>];

export const mappingStateSelector = createSelector(
	mappingSelector,
	(newState: MappingState | null) => newState,
);

export const useMappingState = createHook(Store, {
	selector: mappingStateSelector,
});

export type ColumnsVisibleState = {
	numColumnsVisible: number;
	numColumnsHidden: number;
	areAllColumnsVisible: boolean;
};

export type UseColumnsVisibleState = () => [
	ColumnsVisibleState,
	Partial<BoundActions<State, Actions>>,
];

export const columnsVisibleStateSelector = createSelector(
	mappingSelector,
	(mappingState: MappingState | null) => {
		if (mappingState === null) {
			return {
				numColumnsVisible: 0,
				numColumnsHidden: 0,
				areAllColumnsVisible: true,
			};
		}

		const numColumnsVisible = mappingState.columnOrder.filter(
			(colName) => mappingState.columns[colName].isVisible,
		).length;

		const areAllColumnsVisible = mappingState.columnOrder.length === numColumnsVisible;
		const numColumnsHidden = mappingState.columnOrder.length - numColumnsVisible;

		return {
			numColumnsVisible,
			numColumnsHidden,
			areAllColumnsVisible,
		};
	},
);

export const useColumnsVisibleState = createHook(Store, {
	selector: columnsVisibleStateSelector,
});

export type UseNumVisibleUnmappedColumns = () => [number, Partial<BoundActions<State, Actions>>];

export const numVisibleUnmappedColumnsSelector = createSelector(
	mappingSelector,
	(mappingState: MappingState | null) => {
		if (!mappingState) return 0;

		return mappingState.columnOrder.filter(
			(col) => mappingState.columns[col].isVisible && !mappingState.columns[col].field,
		).length;
	},
);

export const useNumVisibleUnmappedColumns = createHook(Store, {
	selector: numVisibleUnmappedColumnsSelector,
});

export type UseNumUnmappedColumnsIncluded = () => [number, Partial<BoundActions<State, Actions>>];

export const numUnmappedColumnsIncludedSelector = createSelector(
	mappingSelector,
	(mappingState: MappingState | null) => {
		if (!mappingState) return 0;

		return mappingState.columnOrder.filter(
			(col) => mappingState.columns[col].isIncluded && !mappingState.columns[col].field,
		).length;
	},
);

export const useNumUnmappedColumnsIncluded = createHook(Store, {
	selector: numUnmappedColumnsIncludedSelector,
});
